// Ported from relay-protocol packages/relay-widget (v0.1.0).
// Independent verification of the addresses the API hands us, before the
// wallet signs anything against them (security report L-003 / L-008, and the
// verifiable half of L-005).
//
// The problem: GET /v1/recovery and GET /v1/deposit-address return `factory`
// and `forwarder`, and the widget used to sign straight against those values.
// A tampered or compromised response could therefore aim `factory.deploy()`
// and `forwarder.execute()/sweepToUser()` at a contract of the attacker's
// choosing and the user would be none the wiser -- the UI would look normal.
//
// The fix has to be careful about WHERE trust comes from, because the obvious
// version of it is circular:
//
//   * Recomputing the CREATE2 address from values that ALSO came from the API
//     proves nothing -- a hostile response would simply supply matching
//     immutables and the derivation would agree with itself.
//   * Calling `factory.computeAddress()` on an API-supplied factory proves
//     nothing either -- an attacker's "factory" can return whatever it likes,
//     consistently.
//
// So exactly one thing is pinned here, in code, out of the API's reach: the
// set of factories this deployment ever issues addresses from. Given a trusted
// factory, the CHAIN then proves the forwarder, read through the user's OWN
// RPC (wagmi's publicClient) rather than our backend. That is the whole
// design: pin the smallest possible root, derive the rest from the chain.
//
// Pinning the factory list rather than the creation code is deliberate. The
// alternative -- embedding StandingForwarder's ~5.7KB creation code plus all
// nine constructor immutables and doing CREATE2 locally -- verifies the same
// fact while adding ten values that must stay in lockstep with the backend, so
// any routing-param change silently breaks the widget's verification. A
// factory address is one value, and `computeAddress` is the factory's own
// derivation, so it cannot drift from what the factory actually deploys.
import type { ReadContractFn } from "../types";
import { forwarderFactoryComputeAbi } from "./abis";
import { isAddress } from "./units";

/** Every StandingForwarderFactory / DepositorForwarderFactory this deployment
 * has issued deposit addresses from, on BSC.
 *
 * Superseded factories stay listed on purpose. A forwarder's factory is pinned
 * per address at issuance time (`standing_addresses.factory`, copied onto each
 * order), so a user recovering an address minted before a rotation legitimately
 * gets an older factory back from the API -- dropping it here would block that
 * user from recovering their own funds, which is a worse outcome than the
 * narrow trust gain. Every entry is one of ours either way.
 *
 * Rotation (see backends/deploy/RUNBOOK-canary.md): ADD the new factory, keep
 * the old ones. */
export const DEFAULT_TRUSTED_FACTORIES: readonly string[] = [
  // Current, deployed 2026-07-28 with the L-023 native-sweep + L-026 events
  // bytecode.
  "0x486BEEc35A9a9915213Ea02AD3709738335F7702", // StandingForwarderFactory
  "0x3438735303911aBe113AdF14b18eE730cC24893b", // DepositorForwarderFactory
  // Superseded by the rotation above -- still trusted for addresses issued
  // before it (see the note on this constant).
  "0xA3B548Ab23043e6B24A087515fe1306379683533", // StandingForwarderFactory
  "0x44317d07378390543d99Ee76374E86C1Ef6cF832", // DepositorForwarderFactory
];

export type VerifyResult = { ok: true } | { ok: false; reason: string };

function sameAddress(a: string | undefined, b: string | undefined): boolean {
  if (!a || !b) return false;
  return a.toLowerCase() === b.toLowerCase();
}

/** Whether `factory` is one this deployment issues addresses from. Compared
 * case-insensitively: the API's checksum casing is not guaranteed to match the
 * literals above, and a casing difference is not a security signal. */
export function isTrustedFactory(factory: string | undefined, trusted: readonly string[]): boolean {
  return trusted.some((t) => sameAddress(t, factory));
}

/**
 * Resolves the effective trusted-factory list from the ops-supplied CDN value
 * (apps.json `relay.forwarderFactories`, flattened by remote-config).
 *
 * The CDN list REPLACES the pinned defaults whenever it carries at least one
 * valid address (2026-07-31 ops decision): S3 is the source of truth for the
 * factory set, so a rotation can retire an old factory -- not just add a new
 * one -- without a redeploy. The pinned DEFAULT_TRUSTED_FACTORIES are only the
 * OFFLINE fallback: an absent/empty CDN value, a fetch failure, or a list
 * where nothing parses as an address keeps the widget working on the shipped
 * set rather than bricking every verification.
 *
 * Security consequence, stated plainly: whoever can write that S3 object
 * controls which factories the pre-signing verification accepts. The bucket's
 * write access must be held to the same bar as a code deploy. A host's
 * explicit `trustedFactories` prop still bypasses this entirely (unchanged).
 */
export function mergeTrustedFactories(remote: readonly unknown[] | undefined): readonly string[] {
  if (!remote || remote.length === 0) return DEFAULT_TRUSTED_FACTORIES;
  const seen = new Set<string>();
  const resolved: string[] = [];
  for (const entry of remote) {
    // CDN JSON is untyped at runtime -- a malformed entry is skipped, never
    // thrown on (the widget must not die on an ops typo).
    if (typeof entry !== "string" || !isAddress(entry)) continue;
    const key = entry.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    resolved.push(entry);
  }
  // Every entry malformed = treat as "no usable CDN value", not as an empty
  // allowlist -- an empty list would fail every recovery closed on a typo.
  return resolved.length > 0 ? resolved : DEFAULT_TRUSTED_FACTORIES;
}

export interface VerifyForwarderArgs {
  /** The factory the API reported. Checked against the pinned allowlist. */
  factory: string | undefined;
  /** The forwarder the API reported. Checked against the factory's own
   * on-chain derivation. */
  forwarder: string | undefined;
  /** The address funds must end up at -- the connected wallet, NOT a value
   * from the API response. This is what makes the check meaningful: it proves
   * the forwarder is derived for THIS user. */
  recipient: string | undefined;
  version: number | undefined;
  trusted?: readonly string[];
}

/**
 * Proves `forwarder` is the address `factory` really deploys for
 * (`recipient`, `version`), by asking the factory on-chain.
 *
 * Fails CLOSED: any inability to complete the check (no client, missing field,
 * an RPC error) returns ok:false. The caller uses this to gate signing, and an
 * unverifiable address is exactly the case where signing should not proceed --
 * treating "couldn't check" as "fine" would hand back the whole guarantee to
 * anyone able to make the RPC call fail.
 */
export async function verifyForwarder(
  readContract: ReadContractFn | undefined,
  chainId: number,
  { factory, forwarder, recipient, version, trusted = DEFAULT_TRUSTED_FACTORIES }: VerifyForwarderArgs,
): Promise<VerifyResult> {
  if (!factory || !forwarder || !recipient || version === undefined) {
    return { ok: false, reason: "address verification is missing inputs (factory/forwarder/recipient/version)" };
  }
  if (!isTrustedFactory(factory, trusted)) {
    return {
      ok: false,
      reason: `factory ${factory} is not a recognized deposit-address factory -- refusing to sign against it`,
    };
  }
  if (!readContract) {
    return { ok: false, reason: "no chain connection available to verify the forwarder address" };
  }

  let derived: string;
  try {
    derived = (await readContract({
      chainId,
      address: factory as `0x${string}`,
      abi: forwarderFactoryComputeAbi,
      functionName: "computeAddress",
      args: [recipient as `0x${string}`, BigInt(version)],
    })) as string;
  } catch (e) {
    return {
      ok: false,
      reason: `could not verify the forwarder address on-chain: ${e instanceof Error ? e.message : String(e)}`,
    };
  }

  if (!sameAddress(derived, forwarder)) {
    return {
      ok: false,
      reason: `forwarder address mismatch: the API returned ${forwarder}, but factory ${factory} derives ${derived} for this account -- refusing to sign`,
    };
  }
  return { ok: true };
}

/**
 * Proves `forwarder` is derived for `recipient` by SOME pinned factory, without
 * being told which one.
 *
 * Used on the deposit path, where the widget never signs against a factory --
 * it only sends funds -- so the question is simply "is this one of our
 * forwarders for this user?". Not needing the API to name the factory makes
 * this strictly stronger than verifyForwarder: there is one less field of the
 * response being taken at face value, and it also means GET /v1/deposit-address
 * does not have to grow a `factory` field.
 *
 * Trying every pinned factory is also what makes this work across target kinds
 * without special-casing: a swap-kind target's forwarder derives from the
 * standing factory and a depositor-kind target's from its own, and either one
 * matching is a pass. Reads run concurrently -- there are a handful of
 * factories, and a per-factory round-trip in sequence would be needless latency
 * in front of a user action.
 *
 * Fails CLOSED, same as verifyForwarder: if no factory derives it -- including
 * because every read failed -- the answer is no.
 */
export async function verifyForwarderFromAnyFactory(
  readContract: ReadContractFn | undefined,
  chainId: number,
  {
    forwarder,
    recipient,
    version,
    trusted = DEFAULT_TRUSTED_FACTORIES,
  }: Omit<VerifyForwarderArgs, "factory">,
): Promise<VerifyResult> {
  if (!forwarder || !recipient || version === undefined) {
    return { ok: false, reason: "address verification is missing inputs (forwarder/recipient/version)" };
  }
  if (!readContract) {
    return {
      ok: false,
      reason:
        "no BSC chain connection available to verify the deposit forwarder -- add BNB Smart Chain (chain 56) to your wagmi config",
    };
  }

  const derived = await Promise.all(
    trusted.map(async (factory) => {
      try {
        return (await readContract({
          chainId,
          address: factory as `0x${string}`,
          abi: forwarderFactoryComputeAbi,
          functionName: "computeAddress",
          args: [recipient as `0x${string}`, BigInt(version)],
        })) as string;
      } catch {
        // One unreachable//non-factory entry must not veto the others.
        return undefined;
      }
    }),
  );

  if (derived.some((d) => sameAddress(d, forwarder))) return { ok: true };
  if (derived.every((d) => d === undefined)) {
    return { ok: false, reason: "could not reach any deposit-address factory on BSC to verify the forwarder" };
  }
  return {
    ok: false,
    reason: `forwarder address mismatch: ${forwarder} is not the address any known factory derives for this account -- refusing to deposit`,
  };
}

/**
 * Whether the deposit address the user is about to send funds to is one we can
 * derive ourselves.
 *
 * This is the honest boundary of L-005. On every bridged route the deposit
 * address is Relay's own open deposit address: it is not a CREATE2 address of
 * ours, nothing on-chain derives it, and no client-side computation can confirm
 * it -- the residual is documented rather than papered over. What the client
 * CAN do on those routes is verify the `forwarder` in the same response
 * (verifyForwarder above), so a response tampered in that field is caught.
 *
 * The direct BSC route is the exception and is fully verifiable: there is
 * nothing for Relay to move, so the deposit address IS the forwarder, and the
 * on-chain check therefore covers the exact address receiving the funds.
 */
export function depositAddressIsForwarder(depositAddress: string | undefined, forwarder: string | undefined): boolean {
  return sameAddress(depositAddress, forwarder);
}
